import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import { publishImage } from "../helpers/imageupload";
import React from "react";

import { getAsset } from "../helpers/helper";
import dummyUser from "../assets/images/dummy/dummy-user.png";
import useChats from "./useChats";
import useChatMessages from "./useChatMessages";
import useSearchConnections from "./useSearchConnections";
import { SOCKET_URL } from "../config/axios";
import {
  useGetChatBlockStatusQuery,
  useSetChatBlockStatusMutation,
} from "../features/chat/chatApiSlice";

const DELETED_MESSAGE_PREVIEW = "This message was deleted";

const getChatPreferenceKey = (profileId, chatId, preference) =>
  `messenger:${profileId}:${chatId}:${preference}`;

const formatConnectionItem = (item, getAvatarUrl) => {
  const profileCandidates = [
    item.shopname,
    item.shopName,
    item.companyname,
    item.companyName,
    item.profileName,
    item.shopTitle,
    item.name,
    item.displayName,
    item.fullname,
  ];
  const profileName =
    profileCandidates.find(
      (n) =>
        n &&
        typeof n === "string" &&
        n.trim() !== "" &&
        n.trim().toUpperCase() !== "N/A",
    ) ||
    (item.businessType && item.businessType.toUpperCase() !== "N/A"
      ? item.businessType
      : "Connected Profile");

  const locationCandidates = [
    item.location,
    item.shopaddress,
    item.shopAddress,
    item.address,
    item.publicAddress,
  ];
  const locationVal = locationCandidates.find(
    (loc) =>
      loc &&
      typeof loc === "string" &&
      loc.trim() !== "" &&
      loc.trim().toUpperCase() !== "N/A",
  );

  const statusVal =
    item.status &&
    typeof item.status === "string" &&
    item.status.trim().toUpperCase() !== "N/A"
      ? item.status
      : "";

  return {
    ...item,
    location: locationVal || "",
    shopname: profileName,
    displayName: item.displayName && item.displayName.toUpperCase() !== "N/A" ? item.displayName : profileName,
    profilePic: getAvatarUrl(item),
    businessType: item.businessType && item.businessType.toUpperCase() !== "N/A" ? item.businessType : "shop",
    shopid: item.shopid,
    userid: item.userid,
    _id: item._id,
    position: item.position,
    status: statusVal,
  };
};

const mergeChatMessages = (serverMessages, localMessages) => {
  const localById = new Map(
    localMessages.map((message) => [String(message._id), message]),
  );
  const serverIds = new Set(
    serverMessages.map((message) => String(message._id)),
  );
  const mergedServerMessages = serverMessages.map((message) => {
    const localMessage = localById.get(String(message._id));
    if (!localMessage?.isDeleted) return message;

    return {
      ...message,
      ...localMessage,
      isDeleted: true,
      content: DELETED_MESSAGE_PREVIEW,
      media: null,
    };
  });
  const localOnlyMessages = localMessages.filter(
    (message) => !serverIds.has(String(message._id)),
  );

  return [...mergedServerMessages, ...localOnlyMessages].sort(
    (first, second) => {
      const firstTime = new Date(
        first.timestamp || first.createdAt || 0,
      ).getTime();
      const secondTime = new Date(
        second.timestamp || second.createdAt || 0,
      ).getTime();
      return firstTime - secondTime;
    },
  );
};

export const useMessenger = (setLgMsgShow, shopid, lgMsgShow) => {
  const socketRef = useRef(null);
  const modalOpenRef = useRef(Boolean(lgMsgShow));

  useEffect(() => {
    modalOpenRef.current = Boolean(lgMsgShow);
  }, [lgMsgShow]);

  const messageInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const searchInputRef = useRef(null);
  const messageAreaRef = useRef(null);
  const modalRef = useRef(null);

  const [selectedUser, setSelectedUser] = useState(null);
  const [restoredChatId, setRestoredChatId] = useState("");
  const [restoredChatProfile, setRestoredChatProfile] = useState(null);
  const [chatRestoreReady, setChatRestoreReady] = useState(false);
  const [chatHistory, setChatHistory] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMessage, setActiveMessage] = useState(null);
  const notificationSound = useRef(null);

  const { name } = useSelector((state) => state.user);

  useEffect(() => {
    const audioSrc = (process.env.PUBLIC_URL || "") + "/notification.wav";
    notificationSound.current = new Audio(audioSrc);
    notificationSound.current.preload = "auto";
    notificationSound.current.crossOrigin = "anonymous";
    notificationSound.current.load();
    notificationSound.current.unlocked = false;

    const unlockAudio = () => {
      if (!notificationSound.current || notificationSound.current.unlocked)
        return;
      notificationSound.current.muted = true;
      notificationSound.current
        .play()
        .then(() => {
          notificationSound.current.pause();
          notificationSound.current.currentTime = 0;
          notificationSound.current.muted = false;
          notificationSound.current.unlocked = true;
        })
        .catch(() => {
          notificationSound.current.muted = false;
        })
        .finally(() => {
          document.removeEventListener("click", unlockAudio);
          document.removeEventListener("keydown", unlockAudio);
        });
    };

    document.addEventListener("click", unlockAudio);
    document.addEventListener("keydown", unlockAudio);

    return () => {
      document.removeEventListener("click", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  const [users, setUsers] = useState([]);
  const [searchedData, setSearchedData] = useState([]);
  const [switchedProfile, setSwitchedProfile] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const [typingStatus, setTypingStatus] = useState({}); // { [chatId]: boolean }
  const [onlineStatus, setOnlineStatus] = useState({}); // { [userId]: { status, lastSeen } }
  const typingTimeoutRef = useRef(null);

  const [replyingTo, setReplyingTo] = useState(null);

  useEffect(() => {
    setUsers((prevUsers) => {
      let changed = false;
      const updatedUsers = prevUsers.map((user) => {
        const history = chatHistory[user._id];
        const latestMessage = history?.[history.length - 1];
        if (
          !latestMessage?.isDeleted ||
          user.message === DELETED_MESSAGE_PREVIEW
        ) {
          return user;
        }

        changed = true;
        return { ...user, message: DELETED_MESSAGE_PREVIEW };
      });
      return changed ? updatedUsers : prevUsers;
    });
  }, [chatHistory]);

  // Refs to hold latest state for socket callbacks
  const selectedUserRef = useRef(selectedUser);
  const chatHistoryRef = useRef(chatHistory);
  const switchedProfileRef = useRef(switchedProfile);
  const nameRef = useRef(name);
  const skipChatRestoreRef = useRef(false);
  const sentMessageIdsRef = useRef(new Set());

  const getCurrentProfileId = () => {
    const profile = switchedProfileRef.current;
    const profileType = String(profile?.businessType || profile?.type || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    const isGeneralPublic =
      profile?.isGeneralPublic === true ||
      profileType === "generalpublic" ||
      profileType === "user";

    if (isGeneralPublic) return nameRef.current?._id;

    return (
      profile?.id ||
      profile?._id ||
      profile?.shopid ||
      profile?.shopId ||
      profile?.serviceid ||
      profile?.companyid ||
      localStorage.getItem("shopid") ||
      nameRef.current?._id
    );
  };

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    chatHistoryRef.current = chatHistory;
  }, [chatHistory]);

  useEffect(() => {
    switchedProfileRef.current = switchedProfile;
  }, [switchedProfile]);

  useEffect(() => {
    nameRef.current = name;
  }, [name]);

  // Helper function to handle Object vs String rendering
  const safeRender = (value) => {
    if (!value) return "";
    let str = "";
    if (typeof value === "object") {
      str = value.text || "";
    } else {
      str = String(value);
    }
    const trimmed = str.trim();
    if (!trimmed || trimmed.toUpperCase() === "N/A") return "";
    return trimmed;
  };

  // Get first non-empty avatar/image from various candidate fields
  const getAvatarUrl = (data) => {
    if (!data) return null;
    const candidates = [
      data.imageUrl,
      data.profilePic,
      data.image,
      data.logo,
      data.profileImage,
      data.avatar,
      data.userimage,
    ];
    for (const c of candidates) {
      if (!c) continue;
      if (Array.isArray(c)) {
        if (
          c.length > 0 &&
          c[0] &&
          typeof c[0] === "string" &&
          c[0].trim() !== "" &&
          c[0].trim().toUpperCase() !== "N/A"
        ) {
          return c[0].trim();
        }
      } else if (
        typeof c === "string" &&
        c.trim() !== "" &&
        c.trim().toUpperCase() !== "N/A"
      ) {
        return c.trim();
      } else if (typeof c === "object") {
        const extracted =
          c.url || c.filename || c.path || c.image || c.imageUrl || c.src;
        if (extracted) {
          if (Array.isArray(extracted)) {
            if (
              extracted.length > 0 &&
              extracted[0] &&
              typeof extracted[0] === "string" &&
              extracted[0].trim() !== "" &&
              extracted[0].trim().toUpperCase() !== "N/A"
            ) {
              return extracted[0].trim();
            }
          } else if (
            typeof extracted === "string" &&
            extracted.trim() !== "" &&
            extracted.trim().toUpperCase() !== "N/A"
          ) {
            return extracted.trim();
          }
        }
      }
    }
    return null;
  };

  // Resolve various forms of stored image references (string, array, absolute URL)
  const safeGetAsset = (val) => {
    if (!val) return dummyUser;
    let v = val;
    if (Array.isArray(v)) v = v[0];
    if (!v) return dummyUser;
    if (typeof v === "object") {
      v =
        v.url ||
        v.filename ||
        v.path ||
        v.image ||
        v.imageUrl ||
        v.src ||
        v.name ||
        null;
      if (Array.isArray(v)) v = v[0];
    }
    if (!v || typeof v !== "string") return dummyUser;
    const s = v.trim();
    if (!s) return dummyUser;
    if (/^https?:\/\//.test(s)) return s;
    try {
      return getAsset(s);
    } catch (e) {
      return s || dummyUser;
    }
  };

  useEffect(() => {
    socketRef.current = io(SOCKET_URL);

    // Listen for connection
    socketRef.current.on("connect", () => {
      const myId = getCurrentProfileId();

      if (myId) {
        socketRef.current.emit("join_user", myId);
      }

      // Join room if a user is selected
      if (selectedUserRef.current) {
        socketRef.current.emit("join_chat", selectedUserRef.current._id);
        socketRef.current.emit("mark_chat_read", {
          chatId: selectedUserRef.current._id,
          userId: myId,
        });
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("messages_read", {
              detail: { chatId: selectedUserRef.current._id, readBy: myId },
            }),
          );
        }
      }
    });

    const getMessagePreview = (message) => {
      if (!message) return "";
      if (message.media) {
        const lower = String(message.media).toLowerCase();
        if (
          /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)(\?|$)/.test(lower) ||
          lower.includes("image/")
        )
          return "Sent You Image";
        if (
          /\.(mp4|mov|avi|mkv|webm|flv|wmv|3gp)(\?|$)/.test(lower) ||
          lower.includes("video/")
        )
          return "Sent You Video";
        if (
          /\.(mp3|wav|ogg|m4a|aac|flac|opus)(\?|$)/.test(lower) ||
          lower.includes("audio/")
        )
          return "Sent You Audio";
        return "Sent You Media";
      }
      return message.content || "";
    };

    // Listen for incoming messages
    socketRef.current.on("receiveMessage", (message) => {
      const currentProfileId = getCurrentProfileId();
      if (
        localStorage.getItem(
          getChatPreferenceKey(currentProfileId, message.chatId, "blocked"),
        ) === "true"
      ) {
        return;
      }

      const previewText = getMessagePreview(message);

      const myId = currentProfileId;
      const sentByMe =
        String(message.sender || message.sendershopid || "") === String(myId);
      if (
        !sentByMe &&
        notificationSound.current &&
        localStorage.getItem(
          getChatPreferenceKey(myId, message.chatId, "muted"),
        ) !== "true"
      ) {
        try {
          notificationSound.current.currentTime = 0;
          notificationSound.current.muted = false;
          const playPromise = notificationSound.current.play();
          if (playPromise && typeof playPromise.then === "function") {
            playPromise.catch((err) => {
              console.warn("Notification sound play prevented:", err);
            });
          }
        } catch (err) {
          console.warn("Notification sound error:", err);
        }
      }

      // 1. Append to the live chat history for the active conversation
      setChatHistory((prev) => {
        const currentHistory = prev[message.chatId] || [];
        // Deduplicate
        if (currentHistory.some((m) => m._id === message._id)) {
          return prev;
        }

        // If we received a message for the CURRENTLY OPEN chat while the modal is open, mark it as read immediately!
        const currentUser = selectedUserRef.current;
        if (
          modalOpenRef.current &&
          currentUser &&
          currentUser._id === message.chatId
        ) {
          const myId = getCurrentProfileId();
          socketRef.current.emit("mark_chat_read", {
            chatId: message.chatId,
            userId: myId,
          });
          message.status = "read"; // optimistically mark this one as read
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("messages_read", {
                detail: { chatId: message.chatId, readBy: myId },
              }),
            );
          }
        }

        return {
          ...prev,
          [message.chatId]: [...currentHistory, message],
        };
      });

      setUsers((prevUsers) => {
        const now = new Date().toISOString();
        const messageId = String(message._id || "");

        const myIds = new Set(
          [
            getCurrentProfileId(),
            nameRef.current?._id,
            nameRef.current?.id,
            name?._id,
            name?.id,
            shopid,
            switchedProfileRef.current?._id,
            switchedProfileRef.current?.id,
            switchedProfileRef.current?.shopid,
            switchedProfileRef.current?.shopId,
            localStorage.getItem("shopid"),
            localStorage.getItem("userId"),
          ]
            .filter(Boolean)
            .map(String),
        );

        const senderId = String(
          message.sender?._id ||
          message.sender ||
          message.sendershopid ||
          message.senderId ||
          "",
        );

        const isKnownSentByMe =
          Boolean(messageId && sentMessageIdsRef.current.has(messageId)) ||
          myIds.has(senderId);

        const activeChatId = String(
          selectedUserRef.current?._id ||
          selectedUserRef.current?.chatId ||
          "",
        );
        const isOpen =
          Boolean(activeChatId && activeChatId === String(message.chatId)) ||
          Boolean(
            modalOpenRef.current &&
            selectedUserRef.current &&
            String(selectedUserRef.current._id) === String(message.chatId)
          );

        const updated = prevUsers.map((u) => {
          if (String(u._id) === String(message.chatId)) {
            const otherParticipantId = String(
              u.otherParticipantData?._id ||
              u.otherParticipantData?.shopid ||
              u.otherParticipantData?.userid ||
              "",
            );
            const isSenderOther =
              Boolean(otherParticipantId) &&
              Boolean(senderId) &&
              otherParticipantId === senderId;
            const sentByMe =
              isKnownSentByMe ||
              (!isSenderOther && Boolean(otherParticipantId) && Boolean(senderId));

            const currentUnread = Number(u.unreadCount) || 0;
            const newUnread =
              sentByMe || isOpen ? 0 : currentUnread + 1;

            return {
              ...u,
              message: previewText,
              updatedAt: now,
              unreadCount: newUnread,
            };
          }
          return u;
        });

        return [...updated].sort(
          (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
        );
      });
    });

    // Listen for deleted messages
    socketRef.current.on("messageDeleted", (data) => {
      const chatId = String(data.chatId || "");
      const messageId = String(data.messageId || "");
      const currentHistory = chatHistoryRef.current[chatId] || [];
      const deletedMessage = currentHistory.find(
        (message) => String(message._id) === messageId,
      );
      const isLatestMessage =
        currentHistory.length > 0 &&
        String(currentHistory[currentHistory.length - 1]._id) === messageId;
      const deletedContent =
        data.content || deletedMessage?.content || DELETED_MESSAGE_PREVIEW;

      setChatHistory((prev) => {
        const history = prev[chatId];
        if (!history) return prev;

        const updatedHistory = history.map((msg) =>
          String(msg._id) === messageId
            ? {
                ...msg,
                isDeleted: true,
                content: deletedContent,
              }
            : msg,
        );

        return { ...prev, [chatId]: updatedHistory };
      });

      setUsers((prev) =>
        prev.map((user) => {
          if (String(user._id) !== chatId) return user;
          const previewMatchesDeletedMessage =
            (deletedMessage?.content || data.content) &&
            String(user.message || "") ===
              String(deletedMessage?.content || data.content);
          return isLatestMessage || previewMatchesDeletedMessage
            ? { ...user, message: DELETED_MESSAGE_PREVIEW }
            : user;
        }),
      );
    });

    // Listen for messages being read
    socketRef.current.on("messages_read", ({ chatId, readBy }) => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("messages_read", {
            detail: { chatId, readBy },
          }),
        );
      }
      setChatHistory((prev) => {
        const currentHistory = prev[chatId];
        if (!currentHistory) return prev;

        // Mark all messages as read
        const updatedHistory = currentHistory.map((msg) => ({
          ...msg,
          status: msg.sender === readBy ? msg.status : "read",
        }));

        return { ...prev, [chatId]: updatedHistory };
      });
      // Clear unread count for that chat in the users list
      setUsers((prev) =>
        prev.map((u) =>
          String(u._id) === String(chatId) ? { ...u, unreadCount: 0 } : u,
        ),
      );
    });

    // Listen for User Online/Offline status
    socketRef.current.on("userStatus", (data) => {
      setOnlineStatus((prev) => ({
        ...prev,
        [data.userId]: { status: data.status, lastSeen: data.lastSeen },
      }));
    });

    socketRef.current.on("initialStatuses", (onlineUserIds) => {
      setOnlineStatus((prev) => {
        const updated = { ...prev };
        onlineUserIds.forEach((uid) => {
          updated[uid] = { status: "online", lastSeen: Date.now() };
        });
        return updated;
      });
    });

    // Listen for Typing indicators
    socketRef.current.on("typing", (data) => {
      setTypingStatus((prev) => ({ ...prev, [data.chatId]: true }));
    });

    socketRef.current.on("stop_typing", (data) => {
      setTypingStatus((prev) => ({ ...prev, [data.chatId]: false }));
    });

    return () => {
      socketRef.current.disconnect();
    };
  }, []);

  // Re-emit join_user if user credentials load AFTER the socket connects
  useEffect(() => {
    if (socketRef.current?.connected) {
      const myId = getCurrentProfileId();
      if (myId) {
        socketRef.current.emit("join_user", myId);
      }
    }
  }, [switchedProfile, name]);

  // Handle typing emissions automatically via input ref
  useEffect(() => {
    const inputEl = messageInputRef.current;
    if (!inputEl) return;

    const handleInput = () => {
      if (!selectedUser || !socketRef.current?.connected) return;

      const myId = getCurrentProfileId();

      socketRef.current.emit("typing", {
        chatId: selectedUser._id,
        senderId: myId,
      });

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current.emit("stop_typing", {
          chatId: selectedUser._id,
          senderId: myId,
        });
      }, 2000);
    };

    inputEl.addEventListener("input", handleInput);
    return () => inputEl.removeEventListener("input", handleInput);
  }, [selectedUser, switchedProfile, name]);

  useEffect(() => {
    const handler = () => {
      setIsFullscreen(document.fullscreenElement === modalRef.current);
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        if (modalRef.current && modalRef.current.requestFullscreen) {
          await modalRef.current.requestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
      }
    } catch (err) {
      console.error("Failed to toggle fullscreen", err);
    }
  };

  useEffect(() => {
    if (selectedUser && socketRef.current?.connected) {
      socketRef.current.emit("join_chat", selectedUser._id);
      // Immediately mark the chat as read when opening it
      const myId = getCurrentProfileId();
      socketRef.current.emit("mark_chat_read", {
        chatId: selectedUser._id,
        userId: myId,
      });
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("messages_read", {
            detail: { chatId: selectedUser._id, readBy: myId },
          }),
        );
      }
    }
  }, [selectedUser, switchedProfile, name]);

  // Scroll to bottom when chat history changes
  useEffect(() => {
    if (messageAreaRef.current) {
      messageAreaRef.current.scrollTop = messageAreaRef.current.scrollHeight;
    }
  }, [chatHistory, selectedUser]);

  // Wait for profile state to resolve before querying; a stale localStorage
  // shop id can otherwise load another profile's chats during refresh.
  const isGeneralProfile = switchedProfile?.isGeneralPublic === true;
  const chatShopId = !switchedProfile
    ? ""
    : isGeneralProfile
      ? name?._id
      : switchedProfile.id ||
        switchedProfile._id ||
        switchedProfile.shopid ||
        switchedProfile.shopId ||
        switchedProfile.serviceid ||
        switchedProfile.companyid ||
        "";
  const chatProcess = isGeneralProfile ? "User" : "Shop";

  // RTK Query uses each profile's cached chat list when switching tabs.
  const {
    currentData: chatsData,
    isLoading: chatsLoading,
    isFetching: chatsFetching,
    error: chatsQueryError,
    refetch: refetchChats,
    createChat,
    createResult,
  } = useChats(chatShopId, chatProcess, {
    skip: !chatShopId || !switchedProfile,
  });
  // Normalize both raw array and API-wrapped chat responses for refreshes.
  useEffect(() => {
    const chatList = [
      chatsData,
      chatsData?.data,
      chatsData?.result,
      chatsData?.chats,
    ].find(Array.isArray);

    if (!switchedProfileRef.current) return;
    if (!chatList) {
      if (!chatsLoading && !chatsFetching) {
        setChatRestoreReady(true);
        skipChatRestoreRef.current = false;
      }
      return;
    }

    const activeChatId = String(
      selectedUserRef.current?._id ||
      selectedUserRef.current?.chatId ||
      restoredChatId ||
      "",
    );
    const visibleChats = [...chatList].map((chat) => {
      if (activeChatId && String(chat._id) === activeChatId) {
        return { ...chat, unreadCount: 0 };
      }
      return chat;
    });
    const sorted = visibleChats.sort(
      (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
    );
    setUsers(sorted);
    setChatRestoreReady(true);

    if (skipChatRestoreRef.current) {
      return;
    }

    // Restore the previously selected chat once the chat list has loaded.
    if (selectedUser) return;
    const profile = switchedProfileRef.current;
    const profileKey =
      profile?.shopid ||
      profile?.id ||
      profile?._id ||
      (profile?.isGeneralPublic ? "user" : "");
    if (!profileKey) return;
    const savedChatId = localStorage.getItem(`messenger-chat:${profileKey}`);
    const match = savedChatId
      ? sorted.find((chat) => String(chat._id) === savedChatId)
      : null;
    if (!match) return;

    const restoredId = String(match._id);
    const savedProfileKey = `messenger-chat-profile:${profileKey}:${restoredId}`;
    let savedChatProfile = null;
    const serializedProfile = localStorage.getItem(savedProfileKey);
    if (serializedProfile) {
      try {
        savedChatProfile = JSON.parse(serializedProfile);
      } catch (error) {
        console.warn(
          "Unable to restore the selected connected profile.",
          error,
        );
      }
    }

    setSelectedUser(match);
    localStorage.setItem(`messenger-chat:${profileKey}`, restoredId);
    setRestoredChatId(restoredId);
    setRestoredChatProfile(
      savedChatProfile || match.otherParticipantData || null,
    );
    if (!savedChatProfile && match.otherParticipantData) {
      localStorage.setItem(
        savedProfileKey,
        JSON.stringify(match.otherParticipantData),
      );
    }
  }, [
    chatsData,
    chatShopId,
    selectedUser,
    chatsLoading,
    chatsFetching,
    switchedProfile,
  ]);

  const currentProfileShopId =
    switchedProfile && !switchedProfile.isGeneralPublic
      ? switchedProfile.shopid ||
        switchedProfile.shopId ||
        switchedProfile.id ||
        switchedProfile.serviceid ||
        switchedProfile.companyid ||
        ""
      : "";

  const {
    currentData: searchResponse,
    isLoading: connectionsLoading,
    isFetching: connectionsFetching,
  } = useSearchConnections(
    {
      userid: name?._id,
      shopid: currentProfileShopId,
      search: searchTerm || "",
    },
    { skip: !name?._id || !switchedProfile },
  );

  const connectionItems = Array.isArray(searchResponse?.result)
    ? searchResponse.result
    : Array.isArray(searchResponse?.data?.result)
      ? searchResponse.data.result
      : null;
  const [connectedShops, setConnectedShops] = useState([]);

  useEffect(() => {
    if (connectionItems) {
      const formattedData = connectionItems.map((item) =>
        formatConnectionItem(item, getAvatarUrl),
      );
      setConnectedShops(formattedData);
      if (searchTerm) {
        setSearchedData(formattedData);
      }
    } else if (!connectionsFetching) {
      setConnectedShops([]);
    }
  }, [connectionItems, searchTerm, connectionsFetching]);

  useEffect(() => {
    if (!searchTerm) {
      setSearchedData([]);
      return;
    }
    if (connectionItems) {
      const formattedData = connectionItems.map((item) =>
        formatConnectionItem(item, getAvatarUrl),
      );
      setSearchedData(formattedData);
    }
  }, [connectionItems, searchTerm, connectionsFetching]);

  const {
    data: messagesData,
    isLoading: messagesLoading,
    isFetching: messagesFetching,
    refetch: refetchMessages,
    sendMessage: sendMessageMutation,
    deleteMessage: deleteMessageMutation,
    clearChat: clearChatMutation,
  } = useChatMessages(
    selectedUser
      ? {
          chatId: selectedUser._id,
          participantId: getCurrentProfileId(),
        }
      : { chatId: "", participantId: "" },
    { skip: !selectedUser?._id },
  );
  const currentProfileId = getCurrentProfileId();
  const chatParticipants = selectedUser?.participants || [];
  const contactParticipantIds = new Set(
    [
      selectedUser?.otherParticipantData?._id,
      selectedUser?.otherParticipantData?.shopid,
      selectedUser?.otherParticipantData?.userid,
      selectedUser?.otherParticipantData?.id,
    ]
      .filter(Boolean)
      .map(String),
  );
  const isContactChatParticipant = (participant) =>
    contactParticipantIds.has(String(participant?._id || participant || ""));
  const fallbackTargetParticipantId =
    contactParticipantIds.values().next().value || "";
  const currentParticipant =
    chatParticipants.find(
      (participant) => !isContactChatParticipant(participant),
    ) || currentProfileId;
  const currentParticipantId = String(
    currentParticipant?._id || currentParticipant || "",
  );
  const targetParticipant =
    chatParticipants.find(isContactChatParticipant) ||
    fallbackTargetParticipantId;
  const targetParticipantId = String(
    targetParticipant?._id || targetParticipant || "",
  );
  const blockStatusArgs = {
    chatId: selectedUser?._id || "",
    participantId: currentParticipantId,
    targetParticipantId,
  };
  const {
    currentData: blockStatusData,
    isSuccess: isContactBlockStatusLoaded,
  } = useGetChatBlockStatusQuery(blockStatusArgs, {
    skip:
      !blockStatusArgs.chatId ||
      !blockStatusArgs.participantId ||
      !blockStatusArgs.targetParticipantId,
  });
  const [setChatBlockStatus] = useSetChatBlockStatusMutation();

  useEffect(() => {
    if (!selectedUser?._id || typeof blockStatusData?.blocked !== "boolean") {
      return;
    }
    const blockedKey = getChatPreferenceKey(
      currentParticipantId,
      selectedUser._id,
      "blocked",
    );
    if (blockStatusData.blocked) {
      localStorage.setItem(blockedKey, "true");
    } else {
      localStorage.removeItem(blockedKey);
      localStorage.removeItem(
        getChatPreferenceKey(
          currentParticipantId,
          selectedUser._id,
          "blockedAt",
        ),
      );
    }
  }, [blockStatusData?.blocked, currentParticipantId, selectedUser?._id]);

  const handleToggleContactBlock = async (blocked) => {
    if (!blockStatusArgs.chatId || !blockStatusArgs.targetParticipantId) return;
    try {
      await setChatBlockStatus({ ...blockStatusArgs, blocked }).unwrap();
      const blockedKey = getChatPreferenceKey(
        currentParticipantId,
        blockStatusArgs.chatId,
        "blocked",
      );
      if (blocked) {
        localStorage.setItem(blockedKey, "true");
        localStorage.setItem(
          getChatPreferenceKey(
            currentParticipantId,
            blockStatusArgs.chatId,
            "blockedAt",
          ),
          String(Date.now()),
        );
      } else {
        localStorage.removeItem(blockedKey);
        localStorage.removeItem(
          getChatPreferenceKey(
            currentParticipantId,
            blockStatusArgs.chatId,
            "blockedAt",
          ),
        );
      }
      return true;
    } catch (error) {
      console.error("Failed to update contact block status", error);
      window.alert(
        "Could not update this contact's block status. Please try again.",
      );
      return false;
    }
  };

  // RTK Query throws "Cannot refetch a query that has not been started yet"
  // when refetch() is called while the query is still in `skip: true` /
  // uninitialized state. This wrapper silences that specific error — the
  // query will start and fetch automatically on the next render once
  // `selectedUser._id` is available and `skip` becomes false.
  const safeRefetchMessages = React.useCallback(() => {
    try {
      const res = refetchMessages();
      if (res && typeof res.catch === "function") {
        res.catch((err) => {
          if (
            !String(err?.message || "").includes("has not been started yet")
          ) {
            console.error("refetchMessages error:", err);
          }
        });
      }
    } catch (err) {
      if (
        err?.message &&
        String(err.message).includes("has not been started yet")
      ) {
        // Query not yet initialized — safe to ignore, it will auto-fetch.
        return;
      }
      console.warn("safeRefetchMessages caught error:", err);
    }
  }, [refetchMessages]);

  // Merge server results with local changes so stale query data can't undo deletion.
  useEffect(() => {
    if (!messagesData || !selectedUser) return;

    const chatId = selectedUser._id;
    const profileId = currentParticipantId;
    const blockedAt = Number(
      localStorage.getItem(
        getChatPreferenceKey(profileId, chatId, "blockedAt"),
      ),
    );
    const isBlockedIncomingMessage = (message) => {
      if (!Number.isFinite(blockedAt) || blockedAt <= 0) return false;
      const senderId = String(message.sender?._id || message.sender || "");
      const messageTime = new Date(
        message.timestamp || message.createdAt || 0,
      ).getTime();
      return (
        senderId !== String(profileId) &&
        Number.isFinite(messageTime) &&
        messageTime > blockedAt
      );
    };
    const visibleServerMessages = messagesData.filter(
      (message) => !isBlockedIncomingMessage(message),
    );
    setChatHistory((prev) => {
      const currentLocal = (prev[chatId] || []).filter(
        (message) => !isBlockedIncomingMessage(message),
      );
      const merged = mergeChatMessages(visibleServerMessages, currentLocal);
      return { ...prev, [chatId]: merged };
    });
  }, [messagesData, selectedUser?._id, lgMsgShow]);

  // Emit join_chat whenever selectedUser changes so the socket always joins the correct room.
  useEffect(() => {
    if (!selectedUser?._id || !socketRef.current?.connected) return;
    socketRef.current.emit("join_chat", selectedUser._id);

    // Mark any existing unread messages as read immediately on opening the chat
    const myId = getCurrentProfileId();
    if (myId) {
      socketRef.current.emit("mark_chat_read", {
        chatId: selectedUser._id,
        userId: myId,
      });
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("messages_read", {
            detail: { chatId: selectedUser._id, readBy: myId },
          }),
        );
      }
    }
  }, [selectedUser?._id]);

  useEffect(() => {
    if (!lgMsgShow || !selectedUser?._id) return;
    safeRefetchMessages();
  }, [lgMsgShow, selectedUser?._id, safeRefetchMessages]);

  const ProfileSelected = (profile, options = {}) => {
    skipChatRestoreRef.current = Boolean(options.userInitiated);
    switchedProfileRef.current = profile;
    setSwitchedProfile(profile);
    setSelectedUser(null);
    setUsers([]);
    setRestoredChatId("");
    setRestoredChatProfile(null);
    setReplyingTo(null);
    setConnectedShops([]);
    setSearchTerm("");
    setSearchedData([]);
    // NOTE: Do NOT clear the persisted chat key here — we want to restore
    // it on the NEXT load (refresh). It will be overwritten naturally when
    // the user picks a new chat under the new profile.
  };

  const handleSearch = (e) => {
    const val = e.target.value || "";
    setSearchTerm(val);
    if (!val) setSearchedData([]);
  };

  const handleSearchDataClick = async (data) => {
    setRestoredChatId("");
    setRestoredChatProfile(data);

    let myposition =
      switchedProfile?.businessType === "generalPublic" ? "User" : "Shop";
    let otherposition =
      data?.position ||
      (data?.businessType === "generalPublic" ? "User" : "Shop");
    // Derive the correct sender ID — User Profile uses name._id, Shop Profile uses shopid
    let mySenderId =
      myposition === "User"
        ? name?._id || name?.id || localStorage.getItem("userid")
        : switchedProfile?.id || localStorage.getItem("shopid") || name?._id || name?.id || localStorage.getItem("userid");

    const otherSenderId =
      data?.position === "User"
        ? data?.userid || data?.userId || data?.id || data?._id
        : data?.shopid || data?.shopId || data?.id || data?._id;

    // Check if chat already exists in users list to open immediately without network lag
    const existingChat = users?.find((u) => {
      const otherId = String(
        u?.otherParticipantData?._id ||
          u?.otherParticipantData?.shopid ||
          u?.otherParticipantData?.userid ||
          "",
      );
      return (
        (otherId && otherId === String(otherSenderId)) ||
        (data?._id && String(u?._id) === String(data?._id))
      );
    });

    if (existingChat) {
      setRestoredChatId(String(existingChat._id));
      setSelectedUser(existingChat);
      const profileKey =
        switchedProfileRef.current?.shopid ||
        switchedProfileRef.current?.id ||
        switchedProfileRef.current?._id ||
        (switchedProfileRef.current?.isGeneralPublic ? "user" : "");
      if (profileKey) {
        localStorage.setItem(
          `messenger-chat-profile:${profileKey}:${existingChat._id}`,
          JSON.stringify(data),
        );
      }
      if (socketRef.current?.connected) {
        socketRef.current.emit("join_chat", existingChat._id);
      }
      setTimeout(() => safeRefetchMessages(), 0);
      setSearchedData([]);
      setSearchTerm("");
      if (searchInputRef.current) searchInputRef.current.value = "";
      return;
    }

    // If sender and receiver have the same ID (e.g. user opening chat for their own shop),
    // switch sender to the logged-in user so it creates a valid user-to-shop chat
    if (String(mySenderId) === String(otherSenderId)) {
      mySenderId = name?._id || name?.id || localStorage.getItem("userid");
      myposition = "User";
    }

    // Optimistically open active conversation dock immediately so user experiences zero lag
    const optimisticOtherData = {
      _id: otherSenderId,
      fullname: data?.shopname || data?.fullname || data?.name || "Chat",
      shopname: data?.shopname || data?.fullname || data?.name || "Shop",
      image: data?.profilePic || data?.image || data?.imageUrl || null,
      position: otherposition,
      location: data?.location || "",
      businessType: data?.businessType || "shop",
      status: data?.status || "",
    };

    const optimisticChat = {
      _id: `pending-${otherSenderId || Date.now()}`,
      message: "",
      updatedAt: new Date().toISOString(),
      businessType: switchedProfile?.businessType,
      otherbusinessType: data?.businessType,
      otherParticipantData: optimisticOtherData,
      participants: [mySenderId, otherSenderId],
    };

    setSelectedUser(optimisticChat);

    // Guard: don't proceed with API call without valid participant IDs
    if (!mySenderId || !otherSenderId) {
      console.warn("Cannot create chat on server: missing participant IDs", {
        mySenderId,
        otherSenderId,
        data,
      });
      return;
    }

    const participants = [mySenderId, otherSenderId];

    let body = {
      participants,
      myposition,
      otherposition,
      businessType: switchedProfile?.businessType,
      otherbusinessType: data?.businessType,
    };

    try {
      // Server now returns 200 for both new and existing chats (alreadyExists flag on duplicates)
      const created = await createChat(body).unwrap();
      const responseData = created?.data || created;
      const newChatId = responseData?._id;

      const otherParticipantData =
        data?.position === "User"
          ? {
              _id: data.userid,
              fullname: data.shopname,
              shopname: data.shopname,
              image: data.profilePic,
              position: data.position,
              location: data.location,
              businessType: data.businessType,
              status: data.status,
            }
          : {
              _id: data.shopid,
              fullname: data.shopname,
              shopname: data.shopname,
              image: data.profilePic,
              position: data.position,
              location: data.location,
              businessType: data.businessType,
              status: data.status,
            };

      const chatEntry = {
        _id: newChatId,
        message: "",
        updatedAt: responseData?.updatedAt || new Date().toISOString(),
        businessType: responseData?.businessType || body.businessType,
        otherbusinessType:
          responseData?.otherbusinessType || body.otherbusinessType,
        otherParticipantData,
        participants: responseData?.participants || participants,
      };

      setUsers((prevUsers) => {
        const exists = prevUsers.find((u) => u._id === newChatId);
        if (exists) return prevUsers;
        return [...prevUsers, chatEntry];
      });

      setSelectedUser(chatEntry);
      setRestoredChatId(String(newChatId));
      const profileKey =
        switchedProfileRef.current?.shopid ||
        switchedProfileRef.current?.id ||
        switchedProfileRef.current?._id ||
        (switchedProfileRef.current?.isGeneralPublic ? "user" : "");
      if (profileKey) {
        localStorage.setItem(
          `messenger-chat-profile:${profileKey}:${newChatId}`,
          JSON.stringify(data),
        );
      }
      // Always refetch messages so re-clicking the same chat reloads correctly
      setTimeout(() => safeRefetchMessages(), 0);
      setSearchedData([]);
      setSearchTerm("");
      if (searchInputRef.current) searchInputRef.current.value = "";
    } catch (err) {
      console.error("Failed to create/open chat", err);
    }
  };

  const handleClickOutside = (event) => {
    if (
      emojiPickerRef.current &&
      !emojiPickerRef.current.contains(event.target) &&
      toggleButtonRef.current &&
      !toggleButtonRef.current.contains(event.target)
    ) {
      setShowEmojiPicker(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);

  const handleFileUpload = (e) => {
    const files = e?.target?.files || (e?.length !== undefined ? e : null);
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      if (file.type && file.type.startsWith("image/")) {
        try {
          const previewUrl = URL.createObjectURL(file);
          setFilePreview(previewUrl);
        } catch {
          setFilePreview(null);
        }
      } else {
        setFilePreview(null);
      }
    } else {
      console.log("No file selected");
    }
  };

  const handleRemoveSelectedFile = () => {
    if (filePreview) {
      try {
        URL.revokeObjectURL(filePreview);
      } catch (err) {
        // ignore
      }
    }
    setSelectedFile(null);
    setFilePreview(null);
    const fileInput = document.getElementById("fileInput");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Clear staged attachment when active chat changes
  useEffect(() => {
    handleRemoveSelectedFile();
  }, [selectedUser?._id]);

  const handleSend = async (customText) => {
    if (isUploadingMedia) return;

    const text =
      customText !== undefined
        ? customText
        : messageInputRef.current
        ? messageInputRef.current.value
        : "";

    // If neither text nor file is present, do nothing
    if ((!text || !text.trim()) && !selectedFile) {
      return;
    }

    if (selectedFile) {
      try {
        setIsUploadingMedia(true);
        const urls = await publishImage([selectedFile]);
        const mediaUrl = urls && urls.length > 0 ? urls[0] : null;

        sendMessage(text, mediaUrl);
        handleRemoveSelectedFile();
        if (messageInputRef.current) {
          messageInputRef.current.value = "";
        }
      } catch (err) {
        console.error("File upload failed", err);
      } finally {
        setIsUploadingMedia(false);
      }
    } else {
      sendMessage(text);
      if (messageInputRef.current) {
        messageInputRef.current.value = "";
      }
    }
  };

  const onEmojiClick = (emojiObject) => {
    if (messageInputRef.current) {
      messageInputRef.current.value += emojiObject.emoji;
      messageInputRef.current.focus();
    }
  };

  const handleClose = () => setLgMsgShow(false);

  const handleCloseActiveChat = () => {
    skipChatRestoreRef.current = true;
    setSelectedUser(null);
    setRestoredChatId("");
    setRestoredChatProfile(null);
    const profile = switchedProfileRef.current;
    const profileKey =
      profile?.shopid ||
      profile?.id ||
      profile?._id ||
      (profile?.isGeneralPublic ? "user" : "");
    if (profileKey) {
      try {
        localStorage.removeItem(`messenger-chat:${profileKey}`);
      } catch (e) {}
    }
  };

  const handleUserClick = async (user) => {
    console.log("iuser--===", user);
    skipChatRestoreRef.current = false;

    setRestoredChatId(String(user._id || ""));
    setRestoredChatProfile(user.otherParticipantData || null);
    setSelectedUser(user);

    // Persist the selected chat so it can be restored after a page refresh.
    // Key it by profile so switching profiles doesn't re-open a stale chat.
    const profileKey =
      switchedProfileRef.current?.shopid ||
      switchedProfileRef.current?.id ||
      switchedProfileRef.current?._id ||
      (switchedProfileRef.current?.isGeneralPublic ? "user" : "");
    if (user._id && profileKey) {
      localStorage.setItem(`messenger-chat:${profileKey}`, String(user._id));
      if (user.otherParticipantData) {
        localStorage.setItem(
          `messenger-chat-profile:${profileKey}:${user._id}`,
          JSON.stringify(user.otherParticipantData),
        );
      }
    }

    if (socketRef.current?.connected) {
      socketRef.current.emit("join_chat", user._id);
      const currentProfileId = getCurrentProfileId();
      if (currentProfileId) {
        socketRef.current.emit("mark_chat_read", {
          chatId: user._id,
          userId: currentProfileId,
        });
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("messages_read", {
              detail: { chatId: user._id, readBy: currentProfileId },
            }),
          );
        }
      }
    }

    // Always refetch messages — ensures re-clicking the same chat re-loads correctly
    setTimeout(() => safeRefetchMessages(), 0);

    // Clear unread count locally when user opens the chat
    setUsers((prev) =>
      prev.map((u) => (u._id === user._id ? { ...u, unreadCount: 0 } : u)),
    );
  };

  const formatTime = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d)) return "";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const sendMessage = (text, mediaUrl = null) => {
    if ((!text || !text.trim()) && !mediaUrl) return;
    if (!selectedUser) return;
    if (
      localStorage.getItem(
        getChatPreferenceKey(
          getCurrentProfileId(),
          selectedUser._id,
          "blocked",
        ),
      ) === "true"
    ) {
      return;
    }

    const activeProfile = switchedProfileRef.current || switchedProfile;
    const activeProfileType = String(
      activeProfile?.businessType || activeProfile?.type || "",
    )
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    const isGeneralPublicProfile =
      activeProfile?.isGeneralPublic === true ||
      activeProfileType === "generalpublic" ||
      activeProfileType === "user";

    const isValidObjectId = (id) =>
      typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id);

    // Identify the other participant in this chat
    const otherId = String(
      selectedUser?.otherParticipantData?._id ||
        selectedUser?.otherParticipantData?.shopid ||
        selectedUser?.otherParticipantData?.userid ||
        "",
    );

    // If chat document has participants, pick the participant that is NOT the other person
    let sendershopid = null;
    if (isGeneralPublicProfile && isValidObjectId(String(name?._id || ""))) {
      sendershopid = String(name._id);
    } else if (
      Array.isArray(selectedUser?.participants) &&
      selectedUser.participants.length > 0
    ) {
      const myPart = selectedUser.participants.find(
        (p) => String(p?._id || p) !== otherId,
      );
      if (myPart) {
        const candidate = String(myPart?._id || myPart);
        if (isValidObjectId(candidate)) {
          sendershopid = candidate;
        }
      }
    }

    // Fallback: active profile or logged-in user
    if (!sendershopid) {
      const candidates = [
        isGeneralPublicProfile ? name?._id : null,
        activeProfile?._id,
        activeProfile?.shopid,
        activeProfile?.id,
        name?._id,
        localStorage.getItem("shopid"),
      ];
      for (const cand of candidates) {
        if (cand && isValidObjectId(String(cand))) {
          sendershopid = String(cand);
          break;
        }
      }
    }

    if (!sendershopid && isValidObjectId(name?._id)) {
      sendershopid = String(name._id);
    }

    const payload = {
      chatId: selectedUser._id,
      content: text || "",
      sendershopid,
      senderBusinessType: isGeneralPublicProfile
        ? "generalPublic"
        : activeProfile?.businessType || activeProfile?.type || "",
      replyTo: replyingTo?._id || null,
      media: mediaUrl,
    };

    const getPreviewText = (content, media) => {
      if (media) {
        const lower = String(media).toLowerCase();
        if (
          /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)(\?|$)/.test(lower) ||
          lower.includes("image/")
        )
          return "Sent You Image";
        if (
          /\.(mp4|mov|avi|mkv|webm|flv|wmv|3gp)(\?|$)/.test(lower) ||
          lower.includes("video/")
        )
          return "Sent You Video";
        if (
          /\.(mp3|wav|ogg|m4a|aac|flac|opus)(\?|$)/.test(lower) ||
          lower.includes("audio/")
        )
          return "Sent You Audio";
        return "Sent You Media";
      }
      return content || "";
    };

    const previewText = getPreviewText(text, mediaUrl);

    (async () => {
      try {
        const newMessage = await sendMessageMutation(payload).unwrap();
        setReplyingTo(null); // Clear reply state
        if (newMessage?._id) {
          sentMessageIdsRef.current.add(String(newMessage._id));
        }

        // Add to local chat history immediately (optimistic insert for sender).
        // The receiveMessage socket event will also fire back – the _id-based
        // deduplication in the receiveMessage handler will discard that duplicate.
        setChatHistory((prev) => {
          const current = prev[selectedUser._id] || [];
          // Guard: don't re-add if already present
          if (current.some((m) => String(m._id) === String(newMessage._id)))
            return prev;
          return {
            ...prev,
            [selectedUser._id]: [...current, newMessage],
          };
        });

        // Bump the chat to the top for the sender
        setUsers((prevUsers) => {
          const now = new Date().toISOString();
          const updated = prevUsers.map((u) => {
            if (String(u._id) === String(selectedUser._id)) {
              return { ...u, message: previewText, updatedAt: now, unreadCount: 0 };
            }
            return u;
          });
          return [...updated].sort(
            (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
          );
        });

        // Emit via socket so the RECEIVER gets it in real-time.
        // We do NOT need to do anything extra for the sender since we already added it above.
        if (socketRef.current?.connected) {
          socketRef.current.emit("sendMessage", {
            ...newMessage,
            chatId: selectedUser._id,
          });
        }
      } catch (err) {
        console.error(err);
        if (err?.data?.code === "CONTACT_BLOCKED") {
          window.alert(
            "This message could not be sent because one participant has blocked the other.",
          );
        }
      }
    })();
  };

  const handleClearChat = async () => {
    if (!selectedUser?._id) return;

    const chatId = selectedUser._id;
    try {
      await clearChatMutation({
        chatId,
        participantId: getCurrentProfileId(),
      }).unwrap();
      setChatHistory((prev) => ({
        ...prev,
        [chatId]: [],
      }));
      setUsers((prev) =>
        prev.map((user) =>
          String(user._id) === String(chatId)
            ? { ...user, message: "" }
            : user,
        ),
      );
      safeRefetchMessages();
    } catch (error) {
      console.error("Failed to clear chat", error);
      window.alert("Could not clear this conversation. Please try again.");
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteMessageMutation(messageId).unwrap();
      const chatId = selectedUser?._id;
      if (chatId) {
        const currentHistory = chatHistoryRef.current[chatId] || [];
        const deletedIndex = currentHistory.findIndex(
          (message) => String(message._id) === String(messageId),
        );
        const wasLatestMessage = deletedIndex === currentHistory.length - 1;
        const deletedMessage = currentHistory[deletedIndex];
        setChatHistory((prev) => {
          const history = prev[chatId];
          if (!history) return prev;

          const updatedHistory = history.map((message) =>
            String(message._id) === String(messageId)
              ? {
                  ...message,
                  isDeleted: true,
                  content: DELETED_MESSAGE_PREVIEW,
                }
              : message,
          );
          return { ...prev, [chatId]: updatedHistory };
        });
        if (wasLatestMessage || deletedMessage) {
          setUsers((prev) =>
            prev.map((user) => {
              if (String(user._id) !== String(chatId)) return user;
              const previewMatchesDeletedMessage =
                deletedMessage?.content &&
                String(user.message || "") === String(deletedMessage.content);
              return wasLatestMessage || previewMatchesDeletedMessage
                ? { ...user, message: DELETED_MESSAGE_PREVIEW }
                : user;
            }),
          );
        }
      }
      handleCloseMenu(); // Close the menu
    } catch (err) {
      console.error("Failed to delete message", err);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return {
    selectedUser,
    restoredChatId,
    restoredChatProfile,
    chatRestoreReady,
    messagesLoading: Boolean(messagesLoading || messagesFetching),
    setRestoredChatId,
    setRestoredChatProfile,
    setSelectedUser,
    chatHistory,
    setChatHistory,
    anchorEl,
    setAnchorEl,
    activeMessage,
    setActiveMessage,
    name,
    currentProfileId: getCurrentProfileId(),
    isContactBlocked: Boolean(blockStatusData?.blocked),
    isContactBlockStatusLoaded,
    users,
    chatsLoading: Boolean(chatsLoading || chatsFetching),
    chatsError: chatsQueryError
      ? chatsQueryError?.data?.message ||
        chatsQueryError?.error ||
        "Unable to load chats."
      : "",
    refetchChats,
    connectionsLoading: Boolean(connectionsLoading || connectionsFetching),
    searchedData,
    connectedShops,
    switchedProfile,
    searchTerm,
    isFullscreen,
    showEmojiPicker,
    setShowEmojiPicker,
    messageInputRef,
    emojiPickerRef,
    toggleButtonRef,
    searchInputRef,
    messageAreaRef,
    modalRef,
    safeRender,
    getAvatarUrl,
    safeGetAsset,
    toggleFullscreen,
    ProfileSelected,
    handleSearch,
    handleSearchDataClick,
    handleFileUpload,
    selectedFile,
    filePreview,
    isUploadingMedia,
    handleRemoveSelectedFile,
    handleSend,
    onEmojiClick,
    handleClose,
    handleUserClick,
    formatTime,
    sendMessage,
    typingStatus,
    onlineStatus,
    replyingTo,
    setReplyingTo,
    handleDeleteMessage,
    handleClearChat,
    handleToggleContactBlock,
    handleCloseMenu,
    handleCloseActiveChat,
  };
};

export default useMessenger;
